/**
 * Normalize a date value (which may arrive from the backend as a Jackson
 * timestamp array `[year, month, day]` for `LocalDate` or
 * `[year, month, day, hour, minute, ...]` for `LocalDateTime`, or as an
 * ISO string) into a `yyyy-MM-dd` string suitable for `<input type="date">`.
 *
 * Returns an empty string for null/undefined/empty/invalid input.
 */
export function toDateInput(v: string | number[] | null | undefined): string {
  if (v == null || v === '') return '';
  if (Array.isArray(v)) {
    const [y, m, d] = v;
    if (!y || !m || !d) return '';
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
}

/**
 * Convert a backend date value (Jackson array `[y,m,d]`/`[y,m,d,h,mi,s]` or
 * ISO string) to a Date object. Returns null for null/invalid input.
 */
export function parseBackendDate(v: string | number[] | null | undefined): Date | null {
  if (v == null || v === '') return null;
  if (Array.isArray(v)) {
    const [y, m, d, h = 0, mi = 0, s = 0] = v;
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d, h, mi, s);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(String(v));
  return isNaN(dt.getTime()) ? null : dt;
}

/**
 * Format a backend date value for display using the given Intl locale.
 * Returns an empty string for null/invalid input.
 */
export function formatDateDisplay(v: string | number[] | null | undefined, locale = 'cs-CZ'): string {
  const dt = parseBackendDate(v);
  return dt ? dt.toLocaleDateString(locale) : '';
}

/**
 * Format a backend datetime value for display (date + time) using the given Intl locale.
 * Returns an empty string for null/invalid input.
 */
export function formatDateTimeDisplay(
  v: string | number[] | null | undefined,
  locale = 'cs-CZ',
  opts?: Intl.DateTimeFormatOptions
): string {
  const dt = parseBackendDate(v);
  return dt ? dt.toLocaleString(locale, opts) : '';
}
